import {Component, OnInit} from '@angular/core';
import {GoogleMap, MapInfoWindow, MapMarker} from '@angular/google-maps';
import {CommonModule} from '@angular/common';
import {SquareButtonComponent} from '../../shared/components/square-button/square-button.component';
import {InfoPageHeaderComponent} from '../../shared/components/info-page-header/info-page-header.component';
import {MarkersService} from '../../core/api/markers.service';
import {Subject, takeUntil} from 'rxjs';
import {Marker, Markers} from '../../shared/models/markers.model';
import {ReservationService} from '../../core/api/reservation.service';
import {Reservation} from '../../shared/models/reservation.model';

@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  imports: [
    GoogleMap,
    MapMarker,
    CommonModule,
    SquareButtonComponent,
    InfoPageHeaderComponent,
    MapInfoWindow
  ],
  styleUrls: ['./map.component.scss']
})
export class MapComponent implements OnInit {
  private destroy$ = new Subject<void>();
  markers: Markers = [];
  center: google.maps.LatLngLiteral = { lat: 40.416775, lng: -3.703790 };
  zoom = 15;

  reservationPosition: google.maps.LatLngLiteral | null = null;

  reservationMarkerOptions: google.maps.MarkerOptions = {
    draggable: false,
    zIndex: 9999,
    icon: {
      url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
      scaledSize: new google.maps.Size(44, 44),
      anchor: new google.maps.Point(22, 44)
    }
  };

  markerOptions: google.maps.MarkerOptions = {
    draggable: false
  };
  markerPositions: google.maps.LatLngLiteral[] = [];
  activeInfoWindow: number | null = null;
  private openedInfoWindow: MapInfoWindow | null = null;
  selectedMarker: any | null = null;

  constructor(private markersService: MarkersService, private reservationService: ReservationService) {
  }

  ngOnInit(): void {
    this.loadMarkers();
  }

  public loadMarkers(): void {
    this.reservationService.getReservation().subscribe({
      next: (reservation: Reservation) => {
        const lat = parseFloat(reservation.data.apartment.lat);
        const lng = parseFloat(reservation.data.apartment.long);

        this.center = { lat, lng };
        this.reservationPosition = { lat, lng };
      },
      error: (error) => {
        console.error('Error al obtener posición de apartamento:', error);
      }
    });

    this.markersService.getMarkers()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (markers: Markers) => {
          this.markers = markers;
        },
        error: (error) => {
          console.error('Error loading markers:', error);
        }
      });
  }

  private centerMapOnFirstMarker(firstMarker: Marker): void {
    this.center = {
      lat: parseFloat(firstMarker.lat),
      lng: parseFloat(firstMarker.lng)
    };
  }

  getMarkerOptions(marker: Marker): google.maps.MarkerOptions {
    const baseOptions = {
      draggable: false,
      icon: this.getMarkerIcon(marker)
    };

    return baseOptions;
  }

  // Método para obtener el icono según el tipo de marker
  private getMarkerIcon(marker: Marker): google.maps.Icon {
    const baseConfig = {
      scaledSize: new google.maps.Size(32, 32),
      anchor: new google.maps.Point(16, 32)
    };

    switch (marker.type) {
      case 'around':
        // Diferente icono según la sección
        if (marker.section === 'comer') {
          return {
            url: 'https://maps.google.com/mapfiles/ms/icons/restaurant.png',
            ...baseConfig
          };
        } else {
          return {
            url: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png',
            ...baseConfig
          };
        }

      case 'tourist_site':
        return {
          url: 'https://maps.google.com/mapfiles/ms/icons/camera.png',
          ...baseConfig
        };

      case 'recommendation':
        return {
          url: 'https://maps.google.com/mapfiles/ms/icons/star.png',
          ...baseConfig
        };

      default:
        return {
          url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
          ...baseConfig
        };
    }
  }

  openInfoWindow(i: number, marker: MapMarker, infoWindow: MapInfoWindow): void {
    if (this.openedInfoWindow && this.openedInfoWindow !== infoWindow) {
      this.openedInfoWindow.close();
    }
    this.activeInfoWindow = i;
    this.selectedMarker = this.markers?.[i] ?? null;
    infoWindow.open(marker);
    this.openedInfoWindow = infoWindow;
  }

  closeInfoWindow(infoWindow: MapInfoWindow): void {
    this.activeInfoWindow = null;
    this.openedInfoWindow = null;
    infoWindow.close();
  }

  getMarkerImage(marker: Marker): string | null {
    if (!marker.image) return null;

    if (typeof marker.image === 'string') {
      return marker.image;
    }

    if (Array.isArray(marker.image) && marker.image.length > 0) {
      return marker.image[0];
    }

    return null;
  }

  getMarkerTypeText(marker: Marker): string {
    return marker.title;
  }

  navigateToSelectedMarker(): void {
    const m = this.selectedMarker;
    if (!m) return;

    const lat = Number(m.lat);
    const lng = Number(m.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

    const label = typeof m.title === 'string' ? m.title : '';
    const encodedLabel = encodeURIComponent(label);

    const ua = navigator.userAgent || '';
    const isIOS = /iPad|iPhone|iPod/.test(ua);

    // iOS: Apple Maps suele abrir la app Maps
    // Android: geo: suele abrir la app de mapas predeterminada
    // Fallback: URL web de Google Maps (abre app si está configurada / disponible)
    const url = isIOS
      ? `https://maps.apple.com/?daddr=${lat},${lng}&dirflg=d`
      : `geo:${lat},${lng}?q=${lat},${lng}(${encodedLabel})`;

    try {
      // En móvil, location.href suele disparar el handler de la app
      window.location.href = url;
    } catch {
      // Fallback web
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, '_blank');
    }
  }

}
